import { MarkerClusterer } from '@googlemaps/markerclusterer'
import { AdvancedMarker, AdvancedMarkerAnchorPoint, useMap } from '@vis.gl/react-google-maps'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import HubsInfoWindow from '../../components/InfoWindow/HubsInfoWindow'
import VehicleTelematicsInfoWindow from '../../components/InfoWindow/VehicleTelematicInfoWindow'

const VehicleMarker = ({ markerData, onClick, setMarkerRef }) => {
  const handleClick = useCallback(() => onClick(markerData), [onClick, markerData])
  const ref = useCallback((marker) => setMarkerRef(marker, markerData.record.id), [setMarkerRef, markerData.record.id])

  const { Icon, markerType } = markerData

  return (
    <AdvancedMarker
      position={markerData.position}
      anchorPoint={AdvancedMarkerAnchorPoint.CENTER}
      ref={ref}
      onClick={handleClick}
    >
      <Icon type={markerType} {...markerData.record} />
    </AdvancedMarker>
  )
}

export const ClusteredMarkers = ({ telemetries, selectedTelemetryKey, setSelectedTelemetryKey, setHoverInfo }) => {
  const [markers, setMarkers] = useState({})
  const [zoom, setZoom] = useState(0)
  const zoomThreshold = 15
  const suppressClose = useRef(false)

  const selectedTelemetry = useMemo(() => {
    if (!selectedTelemetryKey) return null
    return telemetries.find((t) => t.record.id === selectedTelemetryKey)
  }, [telemetries, selectedTelemetryKey])

  const map = useMap()
  const clustererRef = useRef(null)

  // --- separate event vs normal telemetries -----------------

  const { eventTelemetries, normalTelemetries } = useMemo(() => {
    const eventTypes = ['hub', 'parking', 'fueling', 'last']
    const eventTelemetries = []
    const normalTelemetries = []

    telemetries.forEach((t) => {
      if (eventTypes.includes(t.markerType)) eventTelemetries.push(t)
      else if (t.markerType === 'normal') normalTelemetries.push(t)
      else throw new Error(`Unknown telemetry markerType: ${t.markerType}`)
    })

    return { eventTelemetries, normalTelemetries }
  }, [telemetries])

  // --- zoom listener ----------------------------------------
  useEffect(() => {
    if (!map) return
    const listener = map.addListener('zoom_changed', () => {
      setZoom(map.getZoom())
    })
    setZoom(map.getZoom())
    return () => listener.remove()
  }, [map])

  // --- init clusterer once ----------------------------------
  useEffect(() => {
    if (map && !clustererRef.current) {
      clustererRef.current = new MarkerClusterer({ map })
    }

    return () => {
      clustererRef.current?.clearMarkers()
      clustererRef.current?.setMap(null)
      clustererRef.current = null
    }
  }, [map])

  // --- update clusterer with event markers only -------------
  useEffect(() => {
    if (!clustererRef.current) return

    const clusterer = clustererRef.current
    clusterer.clearMarkers()

    const eventKeys = new Set(eventTelemetries.map((t) => t.record.id))
    const eventMarkers = Object.entries(markers)
      .filter(([key]) => eventKeys.has(key))
      .map(([, marker]) => marker)
      .filter(Boolean)

    if (eventMarkers.length > 0) {
      clusterer.addMarkers(eventMarkers)
    }
  }, [eventTelemetries, markers])

  // --- handle marker ref management --------------------------
  const setMarkerRef = useCallback((marker, key) => {
    setMarkers((prev) => {
      if ((marker && prev[key]) || (!marker && !prev[key])) return prev
      if (marker) {
        return { ...prev, [key]: marker }
      } else {
        const { [key]: _, ...rest } = prev
        return rest
      }
    })
  }, [])

  // --- InfoWindow handlers ----------------------------------
  const handleInfoWindowClose = useCallback(() => {
    if (suppressClose.current) suppressClose.current = false
    else setSelectedTelemetryKey(null)
  }, [])
  const handleMarkerClick = useCallback((telemetry) => {
    suppressClose.current = true
    setHoverInfo(null)
    setSelectedTelemetryKey(telemetry.record.id)
  }, [])

  const visibleTelemetries = eventTelemetries.concat(zoom < zoomThreshold ? [] : normalTelemetries)

  return (
    <>
      {visibleTelemetries.map((telemetry) => (
        <VehicleMarker
          key={telemetry.record.id}
          markerData={telemetry}
          onClick={handleMarkerClick}
          setMarkerRef={setMarkerRef}
        />
      ))}

      {selectedTelemetry &&
        (selectedTelemetry.markerType === 'hub' ? (
          <HubsInfoWindow
            anchor={markers[selectedTelemetryKey]}
            onClose={handleInfoWindowClose}
            record={selectedTelemetry.record}
          />
        ) : (
          <VehicleTelematicsInfoWindow
            anchor={markers[selectedTelemetryKey]}
            onClose={handleInfoWindowClose}
            record={selectedTelemetry.record}
          />
        ))}
    </>
  )
}
